"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const plates = [{
    title: "Custom Build",
    technique: "Concrete",
    year: "2024",
    placement: "Backyard",
    size: "40x20ft",
    src: "https://rocketcitywaterwizards.com/wp-content/uploads/2026/08/Rocket-City-Water-Wizards-Custom-Pool-Builder-Huntsville-AL-Aerial-View-93.png?v=6ivnf6f9deg",
    srcset: "https://rocketcitywaterwizards.com/wp-content/uploads/2026/08/RCWW-luxury-custom-pool-backyard-huntsville-al.png?v=mvpenstiwui-wvc-srcset",
    sizes: "https://rocketcitywaterwizards.com/wp-content/uploads/2026/08/RCWW-luxury-custom-pool-backyard-huntsville-al.png?v=g6vo9cmj38-wvc-sizes",
    alt: "Modern concrete pool installation",
    aspect: "aspect-[3/4]",
    colStart: "lg:col-start-1",
    colSpan: "lg:col-span-5",
    offset: "lg:mt-0"
  }, {
    title: "System Audit",
    technique: "Technical",
    year: "2024",
    placement: "Pump Room",
    size: "High PSI",
    src: "https://rocketcitywaterwizards.com/wp-content/uploads/2026/08/Gemini_Generated_Image_xu03ugxu03ugxu03.png?v=e4s6vpkuw78",
    srcset: "https://rocketcitywaterwizards.com/wp-content/uploads/2026/08/Gemini_Generated_Image_xu03ugxu03ugxu03.png?v=7qxd24v1ptw-wvc-srcset",
    sizes: "https://rocketcitywaterwizards.com/wp-content/uploads/2026/08/Gemini_Generated_Image_xu03ugxu03ugxu03.png?v=8tbj2sxgocc-wvc-sizes",
    alt: "Technical pool pump system audit",
    aspect: "aspect-[4/5]",
    colStart: "lg:col-start-7",
    colSpan: "lg:col-span-4",
    offset: "lg:mt-24"
  }, {
    title: "Full Renovation",
    technique: "Resurface",
    year: "2023",
    placement: "Decking",
    size: "Full Scale",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1752522/img/pool_renovation_site.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1752522/img/pool_renovation_site.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1752522/img/pool_renovation_site.png-wvc-sizes",
    alt: "Pool renovation and resurfacing project",
    aspect: "aspect-[4/5]",
    colStart: "lg:col-start-2",
    colSpan: "lg:col-span-5",
    offset: "lg:mt-40"
  }, {
    title: "Water Balance",
    technique: "Chemical",
    year: "2024",
    placement: "Surface",
    size: "Optimal",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1752522/img/pool_water_clarity.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1752522/img/pool_water_clarity.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1752522/img/pool_water_clarity.png-wvc-sizes",
    alt: "Crystal clear pool water after treatment",
    aspect: "aspect-[3/4]",
    colStart: "lg:col-start-8",
    colSpan: "lg:col-span-5",
    offset: "lg:mt-[18rem]"
  }, {
    title: "Repair Logic",
    technique: "Structural",
    year: "2023",
    placement: "Piping",
    size: "Precision",
    src: "https://rocketcitywaterwizards.com/wp-content/uploads/2026/08/IMG_2147.jpg?v=d0cuwe52len",
    srcset: "https://rocketcitywaterwizards.com/wp-content/uploads/2026/08/IMG_2147.jpg?v=t4hoefrwnnl-wvc-srcset",
    sizes: "https://rocketcitywaterwizards.com/wp-content/uploads/2026/08/IMG_2147.jpg?v=yfuhba94ya-wvc-sizes",
    alt: "Structural pool piping repair work",
    aspect: "aspect-[4/5]",
    colStart: "lg:col-start-4",
    colSpan: "lg:col-span-5",
    offset: "lg:mt-[26rem]"
  }];
  return <section data-section-id="1369" id="field-reports" className="bg-background text-foreground py-32 md:py-44 overflow-hidden">
      <div className="px-5 md:px-16 lg:px-[5vw] max-w-[1440px] mx-auto">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-8">
            <div className="flex items-center gap-4 mb-10">
              <span className="font-mono text-[11px] md:text-[13px] uppercase tracking-[0.22em] text-primary">
                § REF: 04 &nbsp;
              </span>
              <span className="hidden sm:block h-px w-10 bg-primary" />
            </div>
            <h2 className="font-sans font-semibold text-[2.5rem] sm:text-[3.5rem] md:text-[4.5rem] lg:text-[5rem] leading-[1.1] tracking-tighter text-foreground">
              Project <em className="italic font-serif">reports</em>.
            </h2>
          </div>

          <div className="lg:col-span-4 flex flex-col lg:items-end">
            <Link to="/PreviousWorkPage" aria-label="View previous work">
              <Button variant="ghost" className="group h-auto px-9 py-5 border border-primary bg-primary text-background hover:bg-secondary hover:text-secondary-foreground font-sans uppercase tracking-[0.18em] text-[13px] transition-colors duration-250">
                View all work
                <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </Link>
            <div className="mt-6 hidden lg:flex items-center gap-2">
              <span className="h-px w-[120px] bg-primary" />
              <span className="size-[6px] bg-primary" />
            </div>
          </div>
        </div>

        <div className="mt-24 grid grid-cols-1 lg:grid-cols-12 gap-x-6 gap-y-16 lg:gap-y-0">
          {plates.map((plate, i) => {
          const {
            ref: cardRef,
            isVisible: cardVisible
          } = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.15
          });
          return <div key={i} data-index={i} ref={cardRef} className={`group ${plate.colSpan} ${plate.colStart} ${plate.offset} transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${cardVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: `${i * 80}ms`
          }}>
                <div className={`relative overflow-hidden ${plate.aspect} bg-card border border-border`}>
                  <img src={plate.src} data-wvc-srcset={plate.srcset} data-wvc-sizes={plate.sizes} alt={plate.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.03]" />
                </div>

                <div className="pt-6 transition-transform duration-[400ms] ease-out group-hover:-translate-y-1">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-muted-foreground">
                      ID. {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="size-[5px] bg-primary" />
                  </div>
                  <h3 className="mt-3 font-sans font-medium text-2xl md:text-[1.75rem] leading-tight tracking-tight text-foreground">
                    {plate.title}
                  </h3>
                  <div className="mt-3 h-px w-24 bg-primary origin-left scale-x-0 transition-transform duration-[500ms] ease-out group-hover:scale-x-100" />
                  <p className="mt-4 font-mono text-[12px] md:text-[13px] tracking-[0.04em] text-muted-foreground">
                    {plate.technique}
                    <span className="mx-2 text-primary">·</span>
                    {plate.year}
                    <span className="mx-2 text-primary">·</span>
                    {plate.placement}
                    <span className="mx-2 text-primary">·</span>
                    {plate.size}
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}